/* eslint-disable import/no-unresolved */
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import heroImage from '../../images/on-site-inspection-hero.jpg';

export default function OnSiteCarInspectionPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do I need to bring my car for inspection?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "No. Focus Motors offers on-site car inspection — our inspector visits the car's location directly, whether it's at a seller's home, a dealership, or your own residence.",
        },
      },
      {
        '@type': 'Question',
        name: 'What areas do you cover for on-site inspection?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "On-site car inspection is available across all of Kerala. You just need to share the car's location when booking.",
        },
      },
      {
        '@type': 'Question',
        name: 'How do I book an on-site inspection?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: "Simply book online with the car's location and preferred time. Our inspector will arrive at the specified location to carry out the inspection.",
        },
      },
      {
        '@type': 'Question',
        name: 'Is on-site inspection available for both new and used cars?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. On-site inspection covers both pre-purchase inspection for any car and specialized used car inspection.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does on-site car inspection cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Normal Inspection is priced at Rs.4,999 and Premium Inspection at Rs.7,999, with no extra charge for on-site visits within Kerala.',
        },
      },
    ],
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'On-Site Car Inspection',
    provider: { '@type': 'Organization', name: 'Focus Motors', url: 'https://focusmotors.in' },
    areaServed: { '@type': 'State', name: 'Kerala' },
    url: 'https://focusmotors.in/on-site-car-inspection',
    description:
      'On-site car inspection service in Kerala — our inspector visits your location directly, no need to bring the car anywhere. 130+ point checks.',
    offers: [
      {
        '@type': 'Offer',
        name: 'Normal Inspection',
        price: '4999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
      {
        '@type': 'Offer',
        name: 'Premium Inspection',
        price: '7999',
        priceCurrency: 'INR',
        url: 'https://focusmotors.in/inspection-booking',
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>On-Site Car Inspection in Kerala | Focus Motors</title>
        <meta
          name="description"
          content="On-site car inspection across Kerala — our inspector comes to you, no need to bring the car anywhere. 130+ point checks. Normal Rs.4,999 | Premium Rs.7,999."
        />
        <meta
          name="keywords"
          content="on-site car inspection, mobile car inspection Kerala, car inspection at home, doorstep car inspection Kerala"
        />
        <link rel="canonical" href="https://focusmotors.in/inspection" />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content="On-Site Car Inspection in Kerala | Focus Motors" />
        <meta
          property="og:description"
          content="Our inspector comes to your location — on-site car inspection across Kerala. No need to bring the car anywhere."
        />
        <meta property="og:url" content="https://focusmotors.in/on-site-car-inspection" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      <div
        style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px', fontFamily: 'inherit', color: '#ffffff' }}
      >
        <img
          src={heroImage}
          alt="On-site car inspection service in Kerala"
          style={{
            width: '100%',
            height: 'auto',
            maxHeight: '450px',
            objectFit: 'cover',
            borderRadius: '12px',
            marginBottom: '30px',
          }}
        />

        <h1>On-Site Car Inspection in Kerala</h1>
        <p>
          No need to bring your car anywhere. Focus Motors' inspector comes directly to the car's location — whether
          it's at a seller's place, a dealership, or your own home — and carries out a full 130+ point check.
        </p>
        <p>
          We also offer this as part of our{' '}
          <Link to="/inspection">Pre-Purchase Car Inspection</Link> package.
        </p>
        <h2>Why Choose On-Site Inspection</h2>
        <ul>
          <li>
            <strong>Convenience</strong> — no travel, no waiting at a service center
          </li>
          <li>
            <strong>Real conditions</strong> — car inspected exactly where it is, no last-minute prep by the seller
          </li>
          <li>
            <strong>Faster decisions</strong> — get your report the same day, right where the car is
          </li>
          <li>
            <strong>Available anywhere in Kerala</strong> — just share the location when booking
          </li>
        </ul>

        <h2>How On-Site Inspection Works</h2>
        <ol>
          <li>
            <strong>Book Online</strong> — share the car's location and preferred time
          </li>
          <li>
            <strong>Inspector Arrives</strong> — our certified inspector comes to the location
          </li>
          <li>
            <strong>130+ Point Check</strong> — engine, brakes, electricals, body, documentation, and more
          </li>
          <li>
            <strong>Instant Report</strong> — receive a detailed report with photos on the same day
          </li>
        </ol>

        <h2>Works for Any Car</h2>
        <p>
          On-site inspection applies to both our{' '}
          <a href="https://focusmotors.in/inspection" style={{ color: '#ffa500' }}>
            Pre-Purchase Car Inspection
          </a>{' '}
          and{' '}
          <a href="https://focusmotors.in/used-car-inspection-kerala" style={{ color: '#ffa500' }}>
            Used Car Inspection
          </a>{' '}
          services — whichever you need, our inspector comes to you.
        </p>

        <h2>Inspection Packages</h2>
        <p>
          <strong>Normal Inspection — Rs.4,999</strong>
          <br />
          Complete 130+ point inspection, on-site, no extra charge.
        </p>
        <p>
          <strong>Premium Inspection — Rs.7,999</strong>
          <br />
          Extended diagnostics, delivered at your chosen location.
        </p>
        <a
          href="https://focusmotors.in/inspection-booking"
          style={{
            display: 'inline-block',
            marginTop: '10px',
            padding: '12px 24px',
            background: '#000',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '6px',
          }}
        >
          Book On-Site Inspection
        </a>

        <h2>Available Across Kerala</h2>
        <p>
          Our on-site inspection service covers all of Kerala — Kochi, Thrissur, Kozhikode, Thiruvananthapuram, and
          more. Just tell us where the car is, and we'll be there.
        </p>

        <h2>Frequently Asked Questions</h2>

        <h3>Do I need to bring my car for inspection?</h3>
        <p>
          No. Our inspector visits the car's location directly, whether it's a seller's home, a dealership, or your own
          residence.
        </p>

        <h3>What areas do you cover for on-site inspection?</h3>
        <p>Available across all of Kerala. You just need to share the car's location when booking.</p>

        <h3>How do I book an on-site inspection?</h3>
        <p>
          Book online with the car's location and preferred time. Our inspector will arrive at the specified location.
        </p>

        <h3>Is on-site inspection available for both new and used cars?</h3>
        <p>Yes, it covers both pre-purchase inspection and specialized used car inspection.</p>

        <h3>How much does on-site car inspection cost?</h3>
        <p>
          Normal Inspection at Rs.4,999 and Premium Inspection at Rs.7,999, with no extra charge for on-site visits
          within Kerala.
        </p>
      </div>
    </>
  );
}
